import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import { useFormContext } from "react-hook-form";
import { NavLink } from "react-router-dom";

/**
 * InputPassword - composant compatible react-hook-form (doit être rendu à l'intérieur de <FormProvider>)
 *
 * Props:
 *  - name (string) required
 *  - placeholder (string)
 *  - validationRules (object) -> passed to register
 *  - disabled, className, style
 *  - showCriteria (bool) -> show checklist of rules
 *  - forgotPasswordTo / forgotPasswordLabel (optional) -> a second "?" icon inside the field,
 *    left of the eye, linking to the forgot-password page (login page)
 */
const InputPassword = ({
  name,
  placeholder = "",
  validationRules = {},
  disabled = false,
  className = "",
  style = {},
  showCriteria = false,
  floating = false,
  forgotPasswordTo,
  forgotPasswordLabel,
}) => {
  const { register, formState: { errors }, watch } = useFormContext(); // <-- important
  const [visible, setVisible] = useState(false);
  const passwordValue = watch ? (watch(name) || "") : "";

  // register field (react-hook-form)
  const registration = register ? register(name, validationRules) : {};

  // strength calculation
  const computeStrength = (pwd) => {
    if (!pwd) return { score: 0, label: "Très faible", color: "bg-danger" };

    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++; // bonus longueur
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    switch (score) {
      case 0:
      case 1:
        return { score, label: "Très faible", color: "bg-danger" };
      case 2:
        return { score, label: "Faible", color: "bg-warning" };
      case 3:
        return { score, label: "Moyen", color: "bg-info" };
      case 4:
        return { score, label: "Fort", color: "bg-primary" };
      case 5:
        return { score, label: "Très fort", color: "bg-success" };
      default:
        return { score: 0, label: "Très faible", color: "bg-danger" };
    }
  };

  const strength = computeStrength(passwordValue);
  const progressPercent = Math.round((strength.score / 5) * 100);

  // criteria
  const criteria = [
    { id: "len8", ok: passwordValue.length >= 8, label: "≥ 8 caractères" },
    { id: "len12", ok: passwordValue.length >= 12, label: "≥ 12 caractères" },
    { id: "upper", ok: /[A-Z]/.test(passwordValue), label: "Majuscule (A-Z)" },
    { id: "digit", ok: /[0-9]/.test(passwordValue), label: "Chiffre (0-9)" },
    { id: "special", ok: /[^A-Za-z0-9]/.test(passwordValue), label: "Caractère spécial" },
  ];

  const input = (
    <input
      type={visible ? "text" : "password"}
      name={name}
      id={name}
      className={`form-control ${className} ${errors?.[name] ? "is-invalid" : ""}`}
      placeholder={floating ? " " : (placeholder || " ")}
      autoComplete="new-password"
      disabled={disabled}
      style={{ paddingInlineEnd: forgotPasswordTo ? "4.5rem" : "2.5rem", ...style }}
      {...registration}
    />
  );

  return (
    <>
      <div className="position-relative">
        {floating ? (
          <div className="form-floating">
            {input}
            <label className="form-label">{placeholder}</label>
          </div>
        ) : input}

        {/* always shown (not only once something is typed), so the field reads as revealable */}
        <button
          type="button"
          aria-label={visible ? "Cacher le mot de passe" : "Afficher le mot de passe"}
          onClick={() => setVisible((v) => !v)}
          disabled={disabled}
          className="position-absolute top-50 translate-middle-y btn btn-link p-0"
          style={{ textDecoration: "none", color: "#6c757d", insetInlineEnd: "1rem", zIndex: 5 }}
        >
          <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
        </button>

        {forgotPasswordTo && (
          <NavLink
            to={forgotPasswordTo}
            aria-label={forgotPasswordLabel}
            data-tooltip-id="tooltip"
            data-tooltip-content={forgotPasswordLabel}
            className="position-absolute top-50 translate-middle-y"
            style={{ color: "#6c757d", insetInlineEnd: "2.75rem", zIndex: 5, lineHeight: 1 }}
          >
            <FontAwesomeIcon icon={faCircleQuestion} />
          </NavLink>
        )}
      </div>

      {/* strength bar + label */}
      {showCriteria && passwordValue && (
        <div className="mt-2">
          <div className="progress" style={{ height: 8 }}>
            <div
              className={`progress-bar ${strength.color}`}
              role="progressbar"
              style={{
                width: `${progressPercent}%`,
                transition: "width 220ms ease-in-out",
              }}
              aria-valuenow={strength.score}
              aria-valuemin="0"
              aria-valuemax="5"
            />
          </div>
          <div className="d-flex justify-content-between align-items-center mt-1">
            <small className="text-muted">Force : <strong>{strength.label}</strong></small>
            <small className="text-muted">{progressPercent}%</small>
          </div>
        </div>
      )}

      {/* criteria checklist (optionnel) */}
      {showCriteria && passwordValue && (
        <ul className="list-unstyled small mt-2 mb-0">
          {criteria.map((c) => (
            <li key={c.id} className={c.ok ? "text-success" : "text-muted"}>
              {c.ok ? "✓" : "○"} <span className="ms-1">{c.label}</span>
            </li>
          ))}
        </ul>
      )}

      {errors?.[name] && (
        <div className="invalid-feedback">
          {errors[name].message}
        </div>
      )}

    </>
  );
};

export default InputPassword;
